import styled from "styled-components"
import { purpleColors, violetColors, slateColors, Section, Container, SectionTitle as BaseSectionTitle, SectionDescription as BaseSectionDescription, TitleHighlight } from '../ContentPages/AppLandingPage/styles';
import { ArrowRight, Database, BarChart3, Globe, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const DataSection = styled(Section)`
  background: linear-gradient(to bottom right, ${slateColors[50]}, white, ${purpleColors[50]});
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, ${purpleColors[500]}08 1px, transparent 1px),
      linear-gradient(to bottom, ${purpleColors[500]}08 1px, transparent 1px);
    background-size: 24px 24px;
    pointer-events: none;
  }
`;

const DataContainer = styled(Container)`
  position: relative;
  z-index: 10;
`;

const SectionTitle = styled(BaseSectionTitle)`
  margin-bottom: 1rem;
`;

const SectionDescription = styled(BaseSectionDescription)`
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 3rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem;
  border-radius: 1rem;
  background: linear-gradient(to bottom right, ${slateColors[50]}, white);
  border: 1px solid ${slateColors[100]};
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    border-color: ${purpleColors[200]};
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: linear-gradient(to bottom right, ${purpleColors[500]}, ${violetColors[600]});
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: white;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin-bottom: 0.5rem;
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

const FeatureDescription = styled.p`
  font-size: 0.875rem;
  color: ${slateColors[600]};
  line-height: 1.6;
  margin: 0;
  
  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  padding: 0.875rem 1.75rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  color: white;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  margin: 0 auto;
  
  &:hover {
    background: linear-gradient(to right, ${purpleColors[700]}, ${violetColors[600]});
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const API = () => {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: Database,
      title: t('homeScreen.data.historicalData.title'),
      description: t('homeScreen.data.historicalData.description')
    },
    {
      icon: BarChart3,
      title: t('homeScreen.data.statisticsAnalytics.title'),
      description: t('homeScreen.data.statisticsAnalytics.description')
    },
    {
      icon: Globe,
      title: t('homeScreen.data.multiCountryCoverage.title'),
      description: t('homeScreen.data.multiCountryCoverage.description')
    },
    {
      icon: Download,
      title: t('homeScreen.data.apiDataPacks.title'),
      description: t('homeScreen.data.apiDataPacks.description')
    }
  ];

    return (
    <DataSection>
      <DataContainer>
        <SectionTitle>
          {t('homeScreen.data.title')} <TitleHighlight>{t('homeScreen.data.titleHighlight')}</TitleHighlight> {t('homeScreen.data.titleSuffix')}
        </SectionTitle>
        <SectionDescription>
          {t('homeScreen.data.description')}
        </SectionDescription>
        
        <FeaturesGrid>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <FeatureItem key={index}>
                <IconWrapper>
                  <Icon />
                </IconWrapper>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureItem>
            );
          })}
        </FeaturesGrid>
        
        <CTAWrapper>
          <CTAButton to="/data">
            {t('homeScreen.data.exploreDataOfferings')}
            <ArrowRight />
          </CTAButton>
        </CTAWrapper>
      </DataContainer>
    </DataSection>
    );
  };
  
  export default API;
